import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Search, Bell, Send } from "lucide-react";
import { norm } from "@/lib/normalize";
import { formatDateTime } from "@/lib/datetime";
import TablePager from "@/components/TablePager";

const STATUS_COLORS = {
  sent: "bg-[rgba(46,125,50,0.2)] text-green-300 border-green-800",
  delivered: "bg-[rgba(46,125,50,0.2)] text-green-300 border-green-800",
  read: "bg-[rgba(37,99,235,0.18)] text-blue-300 border-blue-800",
  pending: "bg-[rgba(197,160,89,0.15)] text-gold border-gold-subtle",
  queued: "bg-[rgba(197,160,89,0.15)] text-gold border-gold-subtle",
  failed: "bg-[rgba(211,47,47,0.15)] text-red-300 border-red-900",
  error: "bg-[rgba(211,47,47,0.15)] text-red-300 border-red-900",
};

const prettyType = (t) =>
  (t || "")
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

// Local YYYY-MM-DD (day key) for a datetime string
const toDateKey = (dt) => {
  if (!dt) return "";
  const d = new Date(dt);
  if (isNaN(d)) return String(dt).slice(0, 10);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Local YYYY-MM (month key) for a datetime string
const toMonthKey = (dt) => {
  if (!dt) return "";
  const d = new Date(dt);
  if (isNaN(d)) return String(dt).slice(0, 7);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const isFailed = (status) => ["failed", "error"].includes(norm(status));

export default function Notifications() {
  const { user } = useAuth();
  const canNotify = norm(user?.roleName) === "admin" || norm(user?.roleName) === "manager";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCustomer, setFilterCustomer] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sendingId, setSendingId] = useState(null);

  // On-demand "Notify" dialog state
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyForm, setNotifyForm] = useState({ invoiceNo: "", phone: "" });
  const [notifySaving, setNotifySaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/notification-jobs")
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error("Failed to load notifications"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = rows.filter((n) => {
    if (filterCustomer) {
      const name = (n.customerName || "").toLowerCase();
      if (!name.includes(filterCustomer.toLowerCase())) return false;
    }
    if (filterMonth && toMonthKey(n.updatedAt) !== filterMonth) return false;
    if (filterDate && toDateKey(n.updatedAt) !== filterDate) return false;
    return true;
  });

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, pageCount - 1);
  const pagedRows = filtered.slice(currentPage * pageSize, currentPage * pageSize + pageSize);

  // Reset to first page whenever the filtered set changes.
  useEffect(() => { setPage(0); }, [filterCustomer, filterMonth, filterDate, pageSize]);

  // Resend an invoice for a failed notification row.
  const resend = async (n) => {
    if (!n.invoiceNo) { toast.error("No invoice number on this notification"); return; }
    setSendingId(n.id);
    try {
      await api.post("/invoices/send", { invoiceNo: n.invoiceNo, phone: n.phoneNumber || undefined });
      toast.success(`Invoice ${n.invoiceNo} queued to send`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.response?.data?.message || "Failed to send");
    } finally { setSendingId(null); }
  };

  // Send an on-demand invoice from the top "Notify" dialog.
  const submitNotify = async () => {
    const invoiceNo = notifyForm.invoiceNo.trim();
    if (!invoiceNo) { toast.error("Invoice number is required"); return; }
    setNotifySaving(true);
    try {
      const payload = { invoiceNo };
      if (notifyForm.phone.trim()) payload.phone = notifyForm.phone.trim();
      await api.post("/invoices/send", payload);
      toast.success(`Invoice ${invoiceNo} queued to send`);
      setNotifyOpen(false);
      setNotifyForm({ invoiceNo: "", phone: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.response?.data?.message || "Failed to send");
    } finally { setNotifySaving(false); }
  };

  return (
    <div className="space-y-8" data-testid="notifications-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">Messaging</div>
          <h1 className="font-heading text-4xl text-white inline-flex items-center gap-3">
            <Bell className="w-7 h-7 text-amber-500" /> Notifications
            <span className="font-normal text-gold text-2xl">({filtered.length})</span>
          </h1>
        </div>
        {canNotify && (
          <button
            onClick={() => { setNotifyForm({ invoiceNo: "", phone: "" }); setNotifyOpen(true); }}
            data-testid="notify-btn"
            className="gold-button px-5 py-2.5 rounded-full inline-flex items-center gap-2"
          >
            <Send className="w-4 h-4" /> Notify
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-emerald-muted absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={filterCustomer}
            onChange={(e) => setFilterCustomer(e.target.value)}
            placeholder="Filter by customer…"
            className="pl-9 bg-emerald-surface border-gold-subtle text-white"
            data-testid="notification-customer-filter"
          />
        </div>
        <input
          type="month"
          value={filterMonth}
          onChange={(e) => setFilterMonth(e.target.value)}
          className="h-10 px-3 rounded-md bg-emerald-surface border border-gold-subtle text-white text-sm focus:outline-none"
          data-testid="notification-month-filter"
        />
        <Input
          type="date"
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
          className="bg-emerald-surface border-gold-subtle text-white h-10 w-44"
          data-testid="notification-date-filter"
        />
        {(filterCustomer || filterMonth || filterDate) && (
          <button
            onClick={() => { setFilterCustomer(""); setFilterMonth(""); setFilterDate(""); }}
            className="text-xs text-emerald-muted hover:text-gold underline"
            data-testid="notification-clear-filters"
          >
            Clear filters
          </button>
        )}
      </div>

      <div className="bg-emerald-surface border border-gold-subtle rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="thead-row hover:bg-[rgba(197,160,89,0.06)]">
              <TableHead className="th-gold">Customer</TableHead>
              <TableHead className="th-gold">Type</TableHead>
              <TableHead className="th-gold">Invoice</TableHead>
              <TableHead className="th-gold">Phone</TableHead>
              <TableHead className="th-gold">Date & Time</TableHead>
              <TableHead className="th-gold text-center">Status</TableHead>
              <TableHead className="th-gold text-center w-28">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={7} className="text-center text-emerald-muted py-8">Loading…</TableCell></TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-emerald-muted py-8">No notifications found.</TableCell></TableRow>
            )}
            {!loading && pagedRows.map((n) => (
              <TableRow key={n.id} className="border-gold-subtle" data-testid={`notification-row-${n.id}`}>
                <TableCell className="text-white">{n.customerName || "—"}</TableCell>
                <TableCell className="text-emerald-muted">{prettyType(n.jobType)}</TableCell>
                <TableCell className="text-emerald-muted">{n.invoiceNo || "—"}</TableCell>
                <TableCell className="text-emerald-muted">{n.phoneNumber || "—"}</TableCell>
                <TableCell className="text-emerald-muted">{formatDateTime(n.updatedAt)}</TableCell>
                <TableCell className="text-center">
                  <Badge variant="outline" className={`${STATUS_COLORS[norm(n.status)] || "border-gold-subtle text-emerald-muted"} capitalize`}>
                    {norm(n.status) || "—"}
                  </Badge>
                </TableCell>
                <TableCell className="text-center">
                  {isFailed(n.status) && n.invoiceNo && (
                    <button
                      onClick={() => resend(n)}
                      disabled={sendingId === n.id}
                      data-testid={`notification-resend-${n.id}`}
                      className="text-xs text-gold hover:underline inline-flex items-center gap-1 disabled:opacity-50"
                      title="Resend invoice"
                    >
                      <Send className="w-3 h-3" /> {sendingId === n.id ? "Sending…" : "Send"}
                    </button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!loading && filtered.length > 0 && (
          <TablePager
            page={currentPage}
            pageSize={pageSize}
            total={filtered.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        )}
      </div>

      {canNotify && (
        <Dialog open={notifyOpen} onOpenChange={setNotifyOpen}>
          <DialogContent className="bg-emerald-surface border-gold-subtle text-white max-w-md" data-testid="notify-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">Send Invoice Notification</DialogTitle>
              <DialogDescription className="text-emerald-muted">
                Send an invoice on demand. Phone is optional — leave it blank to use the invoice's customer number.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Invoice No. *</Label>
                <Input
                  value={notifyForm.invoiceNo}
                  onChange={(e) => setNotifyForm((f) => ({ ...f, invoiceNo: e.target.value }))}
                  className="bg-emerald-elev border-gold-subtle mt-1 text-white"
                  placeholder="e.g. INV-0001"
                  data-testid="notify-invoice-input"
                />
              </div>
              <div>
                <Label>Phone (optional)</Label>
                <Input
                  value={notifyForm.phone}
                  onChange={(e) => setNotifyForm((f) => ({ ...f, phone: e.target.value }))}
                  className="bg-emerald-elev border-gold-subtle mt-1 text-white"
                  placeholder="e.g. 9876543210"
                  data-testid="notify-phone-input"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setNotifyOpen(false)}
                  className="px-4 h-10 rounded-md border border-gold-subtle text-emerald-muted hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submitNotify}
                  disabled={notifySaving}
                  className="gold-button px-5 h-10 rounded-md inline-flex items-center gap-2 disabled:opacity-60"
                  data-testid="notify-submit-btn"
                >
                  <Send className="w-4 h-4" /> {notifySaving ? "Sending…" : "Send"}
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
